import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { DATE_FORMAT } from '../../common/constants';

// Styles
import { DocumentRepositoryGridWrapper, DocumentRepositoryWrapper } from './DocumentRepository.styles';

// Components
import { ButtonPrimary, Loading, NoResults, StyledTitle, Table } from '../../common/components';
import InfiniteScroll from 'react-infinite-scroll-component';
import { globalApiParams } from '../../common/api/globals';

import { TagManager } from '../utils/analytics/TagManager';
import { isTpiUser } from '../../helpers/tpiUser';

type DocumentRepositoryPageProps = {
  email: string;
  gridDataSpecificDocuments: Array<Api.IDocumentRepository>;
  gridDataGenericDocuments: Array<Api.IDocumentRepository>;
  handleSpecificDocumentsPagingChange: any;
  handleGenericDocumentsPagingChange: any;
  handleDownloadSpecificDocument: any;
  handleDownloadGenericDocument: any;
  isSpecificLoading?: boolean;
  isGenericLoading?: boolean;
  hasMoreSpecificDocumentData: boolean;
  hasMoreGenericDocumentData: boolean;
  currentSortField: string;
  currentSortOrder: 'ASC' | 'DESC' | '';
};

const defaultProps = {
  email: '',
  gridDataSpecificDocuments: [],
  gridDataGenericDocuments: [],
  isSpecificLoading: false,
  isGenericLoading: false,
  hasMoreSpecificDocumentData: true,
  hasMoreGenericDocumentData: true,
  currentSortField: '',
  currentSortOrder: 'ASC',
};

const DocumentRepositoryPage = (props: DocumentRepositoryPageProps) => {
  const [selectedSpecificDocumentRow, setSelectedSpecificDocumentRow] = useState<number | null>(null);
  const [selectedGenericDocumentRow, setSelectedGenericDocumentRow] = useState<number | null>(null);
  const [isSpecificLoadingEx, setIsSpecificLoadingEx] = useState(false);
  const [isGenericLoadingEx, setIsGenericLoadingEx] = useState(false);

  const scrollableSpecificTable = useRef<any>();
  const scrollableGenericTable = useRef<any>();
  const sortDetails: Common.ISort = { field: props.currentSortField, order: props.currentSortOrder };

  const tpiUser = isTpiUser();

  const scrollToTopSpecificGrid = () => {
    if (scrollableSpecificTable.current) {
      scrollableSpecificTable.current.scrollTo(0, 0);
    }
  };

  const scrollToTopGenericGrid = () => {
    if (scrollableGenericTable.current) {
      scrollableGenericTable.current.scrollTo(0, 0);
    }
  };

  const staticTextTop = 'This page allows you to download documents we have uploaded for you.';

  const staticTextBottom =
    'This includes documents and correspondence we provide for all users, such as correspondence from us, and intelligence and information we provide to our customers and partners.';

  const staticTextBottom2 = 'This page will also include any documents we have tailor-made for you and your business.';

  const staticTextBottom3 = `Please let us know if you require bespoke consumption and Third party cost forecasts by emailing <a href='mailto:${props.email}'>${props.email}</a>`;

  useEffect(() => {
    if (props.gridDataSpecificDocuments.length <= globalApiParams.pageSize) {
      scrollToTopSpecificGrid();
    }
  }, [props.gridDataSpecificDocuments]);

  useEffect(() => {
    if (props.gridDataGenericDocuments.length <= globalApiParams.pageSize) {
      scrollToTopGenericGrid();
    }
  }, [props.gridDataGenericDocuments]);

  const handleSpecificDocumentsPagingChange = () => {
    props.handleSpecificDocumentsPagingChange();
  };

  const handleGenericDocumentsPagingChange = () => {
    props.handleGenericDocumentsPagingChange();
  };

  let headers: Array<Common.HeaderItem> = [
    {
      title: 'Document Id',
      dataValue: 'documentId',
      enableSort: false,
    },
    {
      title: 'Issued',
      dataValue: 'startDate',
      enableSort: false,
    },
    {
      title: 'Document type',
      dataValue: 'type',
      minWidth: 160,
      enableSort: false,
    },
    {
      title: 'Document title',
      dataValue: 'title',
      minWidth: 260,
      enableSort: false,
    },
    {
      title: 'Account reference',
      dataValue: 'references',
      enableSort: false,
    },
    {
      title: 'Available to...',
      dataValue: 'endDate',
      enableSort: false,
    },
  ];

  const handleSpecificDocumentRowSelect = (event: any, row: number | null) => {
    if (row !== undefined) {
      setSelectedSpecificDocumentRow(row);
      setSelectedGenericDocumentRow(null);
    }
  };

  const handleGenericDocumentRowSelect = (event: any, row: number | null) => {
    if (row !== undefined) {
      setSelectedGenericDocumentRow(row);
      setSelectedSpecificDocumentRow(null);
    }
  };

  const actionStyles = {
    width: '150px',
    paddingLeft: '0',
  };

  const handleDownloadSpecificClick = (event: any, row: Api.IDocumentRepository) => {
    event.preventDefault();

    TagManager.pushData({
      event: 'Download document',
      userType: tpiUser ? 'TPI User' : 'Customer',
      customerAccountReference: row.references[0],
      documentId: row.documentId,
    });

    props.handleDownloadSpecificDocument(row.documentId);
  };

  const handleDownloadGenericClick = (event: any, row: Api.IDocumentRepository) => {
    event.preventDefault();

    TagManager.pushData({
      event: 'Download document',
      userType: tpiUser ? 'TPI User' : 'Customer',
      customerAccountReference: row.references[0],
      documentId: row.documentId,
    });
    props.handleDownloadGenericDocument(row.documentId);
  };

  const getSpecificActions = (row: any) => (
    <ButtonPrimary title='Download' onClick={e => handleDownloadSpecificClick(e, row)} className='col m-0' />
  );

  const getGenericActions = (row: any) => (
    <ButtonPrimary title='Download' onClick={e => handleDownloadGenericClick(e, row)} className='col m-0' />
  );

  const renderDate = (value: any) => {
    if (value) {
      return <td>{moment(value).format(DATE_FORMAT)}</td>;
    } else {
      return <td></td>;
    }
  };

  const renderNotApplicable = (value: any) => {
    return <td>N/A</td>;
  };

  const renderAccountReferences = (value: Array<string>) => {
    if (value) {
      return (
        <td className='acc-refs-col' title={value.join(', ')}>
          {value.join(', ')}
        </td>
      );
    } else {
      return <td></td>;
    }
  };

  return (
    <DocumentRepositoryWrapper>
      <StyledTitle>My documents</StyledTitle>
      <div className='mt-3 mb-4 static-text'>
        <p>{staticTextTop}</p>
        <p>
          {staticTextBottom} {staticTextBottom2}
        </p>
        <p dangerouslySetInnerHTML={{ __html: staticTextBottom3 }} />
      </div>

      <div className='' style={{ height: '55vh' }}>
        <div className='no-gutters' style={{ height: '50%' }}>
          <StyledTitle className='pb-2' fontSize='21px'>
            Specific documents
          </StyledTitle>

          <div className='col-12' style={{ margin: 0, padding: 0, height: '100%' }}>
            {!props.isSpecificLoading && props.gridDataSpecificDocuments.length === 0 && <NoResults />}
            <DocumentRepositoryGridWrapper>
              {props.gridDataSpecificDocuments.length > 0 && (
                <>
                  <InfiniteScroll
                    dataLength={props.gridDataSpecificDocuments.length}
                    next={handleSpecificDocumentsPagingChange}
                    hasMore={props.hasMoreSpecificDocumentData}
                    loader={''}
                    scrollableTarget='scrollableSpecificDiv'
                    scrollThreshold={0.9}
                  >
                    <div
                      id='scrollableSpecificDiv'
                      ref={scrollableSpecificTable}
                      className='table-responsive fixed-column columns-1'
                    >
                      <Table
                        headers={headers}
                        actions={getSpecificActions}
                        rows={props.gridDataSpecificDocuments}
                        sortDetails={sortDetails}
                        onClickSort={() => {}}
                        actionStyles={actionStyles}
                        selectedRowIndex={selectedSpecificDocumentRow}
                        handleSelectRow={handleSpecificDocumentRowSelect}
                        className='list table plain text-left'
                        renderstartDate={renderDate}
                        renderendDate={renderDate}
                        renderreferences={renderAccountReferences}
                      />
                    </div>
                  </InfiniteScroll>
                </>
              )}

              {(props.isSpecificLoading || isSpecificLoadingEx) && <Loading overlay nowait />}
            </DocumentRepositoryGridWrapper>
          </div>
        </div>

        <div className='no-gutters mt-4' style={{ height: '50%' }}>
          <StyledTitle className='pb-2' fontSize='21px'>
            Generic documents
          </StyledTitle>
          <div className='col-12' style={{ margin: 0, padding: 0, height: '100%' }}>
            {!props.isGenericLoading && props.gridDataGenericDocuments.length === 0 && <NoResults />}
            <DocumentRepositoryGridWrapper>
              {props.gridDataGenericDocuments.length > 0 && (
                <>
                  <InfiniteScroll
                    dataLength={props.gridDataGenericDocuments.length}
                    next={handleGenericDocumentsPagingChange}
                    hasMore={props.hasMoreGenericDocumentData}
                    loader={''}
                    scrollableTarget='scrollableGenericDiv'
                    scrollThreshold={0.9}
                  >
                    <div
                      id='scrollableGenericDiv'
                      ref={scrollableGenericTable}
                      className='table-responsive fixed-column columns-1'
                    >
                      <Table
                        headers={headers}
                        actions={getGenericActions}
                        rows={props.gridDataGenericDocuments}
                        sortDetails={sortDetails}
                        onClickSort={() => {}}
                        actionStyles={actionStyles}
                        selectedRowIndex={selectedGenericDocumentRow}
                        handleSelectRow={handleGenericDocumentRowSelect}
                        className='list table plain text-left'
                        renderstartDate={renderDate}
                        renderendDate={renderDate}
                        renderreferences={renderNotApplicable}
                      />
                    </div>
                  </InfiniteScroll>
                </>
              )}

              {(props.isGenericLoading || isGenericLoadingEx) && <Loading overlay nowait />}
            </DocumentRepositoryGridWrapper>
          </div>
        </div>
      </div>
    </DocumentRepositoryWrapper>
  );
};

DocumentRepositoryPage.defaultProps = defaultProps;

export default DocumentRepositoryPage;
